import { useUnderwritingStore } from '../../store/underwritingStore'

const fmt = (n) => '$' + n.toLocaleString()
const pct = (n) => (n * 100).toFixed(2) + '%'

const THRESHOLDS = {
  capRate:    { min: 0.05,  label: 'Min 5.0%',  pass: (v) => v >= 0.05 },
  dscr:       { min: 1.25,  label: 'Min 1.25x', pass: (v) => v >= 1.25 },
  cashOnCash: { min: 0.06,  label: 'Min 6.0%',  pass: (v) => v >= 0.06 },
  irr:        { min: 0.12,  label: 'Min 12.0%', pass: (v) => v >= 0.12 },
}

function MetricRow({ label, value, display, threshold, explain }) {
  const ok = threshold.pass(value)
  return (
    <div className={`uw-metric-row ${ok ? 'uw-metric-row--pass' : 'uw-metric-row--fail'}`}>
      <div className="uw-metric-left">
        <span className={`uw-metric-check ${ok ? 'uw-check--pass' : 'uw-check--fail'}`}>
          {ok ? '✓' : '✗'}
        </span>
        <div>
          <div className="uw-metric-name">{label}</div>
          <div className="uw-metric-explain">{explain}</div>
        </div>
      </div>
      <div className="uw-metric-right">
        <div className={`uw-metric-value ${ok ? 'uw-value--pass' : 'uw-value--fail'}`}>
          {display}
        </div>
        <div className="uw-metric-threshold">{threshold.label}</div>
        <div className="uw-metric-bar-wrap">
          <div
            className={`uw-metric-bar ${ok ? 'uw-bar--pass' : 'uw-bar--fail'}`}
            style={{ width: `${Math.min(100, (value / (threshold.min * 2)) * 100)}%` }}
          />
          <div
            className="uw-metric-bar-target"
            style={{ left: '50%' }}
          />
        </div>
      </div>
    </div>
  )
}

export default function MetricsPanel({ results, passCount }) {
  const { activeScenario, scenarios } = useUnderwritingStore()
  const s = scenarios[activeScenario]

  return (
    <div className="uw-metrics-layout">

      {/* ── Income & Expense Waterfall ── */}
      <div className="uw-waterfall">
        <h3 className="uw-section-title">Income &amp; Expense Breakdown</h3>
        <div className="uw-waterfall-rows">
          <WRow label="Gross Rental Income"  value={fmt(s.grossRentalIncome)} type="income" />
          <WRow label="Other Income"          value={fmt(s.otherIncome)}       type="income" />
          <WRow label={`Vacancy (${(s.vacancyRate*100).toFixed(0)}%)`} value={fmt(Math.round(s.grossRentalIncome * s.vacancyRate))} type="deduct" />
          <div className="uw-wf-subtotal">
            <span>Effective Gross Income (EGI)</span>
            <span>{fmt(results.egi)}</span>
          </div>
          <WRow label="Property Tax"          value={fmt(s.propertyTax)}  type="expense" />
          <WRow label="Insurance"             value={fmt(s.insurance)}    type="expense" />
          <WRow label="Utilities"             value={fmt(s.utilities)}    type="expense" />
          <WRow label="Maintenance & Repairs" value={fmt(s.maintenance)} type="expense" />
          <WRow label={`Management (${(s.managementPct*100).toFixed(0)}% EGI)`} value={fmt(results.mgmt)} type="expense" />
          <WRow label="Reserves"              value={fmt(s.reserves)}    type="expense" />
          <div className="uw-wf-subtotal uw-wf-subtotal--noi">
            <span>Net Operating Income (NOI)</span>
            <span>{fmt(results.noi)}</span>
          </div>
          <WRow label="Annual Debt Service"   value={fmt(results.annualDebtService)} type="debt" />
          <div className={`uw-wf-subtotal uw-wf-subtotal--cf ${results.cashFlowBeforeTax >= 0 ? 'uw-cf--pos' : 'uw-cf--neg'}`}>
            <span>Cash Flow Before Tax</span>
            <span>{fmt(results.cashFlowBeforeTax)}</span>
          </div>
        </div>
      </div>

      {/* ── Key Metrics ── */}
      <div className="uw-key-metrics">
        <div className="uw-key-metrics-header">
          <h3 className="uw-section-title">Underwriting Metrics</h3>
          <div className={`uw-pass-badge ${passCount === 4 ? 'uw-pass-badge--all' : passCount >= 3 ? 'uw-pass-badge--most' : 'uw-pass-badge--few'}`}>
            {passCount}/4 Criteria Pass
          </div>
        </div>

        <MetricRow
          label="Cap Rate"
          value={results.capRate}
          display={pct(results.capRate)}
          threshold={THRESHOLDS.capRate}
          explain="NOI ÷ Purchase Price. Measures yield independent of financing."
        />
        <MetricRow
          label="DSCR"
          value={results.dscr}
          display={results.dscr.toFixed(2) + 'x'}
          threshold={THRESHOLDS.dscr}
          explain="NOI ÷ Annual Debt Service. Min 1.25x required by most lenders."
        />
        <MetricRow
          label="Cash-on-Cash Return"
          value={results.cashOnCash}
          display={pct(results.cashOnCash)}
          threshold={THRESHOLDS.cashOnCash}
          explain="Annual cash flow ÷ equity invested. Year 1 return on your cash."
        />
        <MetricRow
          label="IRR (5-Year Hold)"
          value={results.irr}
          display={pct(results.irr)}
          threshold={THRESHOLDS.irr}
          explain="Internal rate of return including sale proceeds at exit cap rate."
        />

        {/* Monthly Cost Breakdown */}
        <div className="uw-loan-summary">
          <div className="uw-loan-summary-title">Monthly Cost Breakdown</div>
          <div className="uw-loan-row">
            <span>Debt Service (mo.)</span>
            <span>{fmt(results.monthlyDebtService ?? Math.round(results.annualDebtService / 12))}</span>
          </div>
          <div className="uw-loan-row">
            <span>
              Insurance (mo.)
              {results.insuranceIsEstimated && <em className="uw-est-tag"> est.</em>}
            </span>
            <span>{fmt(results.monthlyInsurance ?? Math.round(s.insurance / 12))}</span>
          </div>
          <div className="uw-loan-row">
            <span>Property Tax (mo.)</span>
            <span>{fmt(results.monthlyPropertyTax ?? Math.round(s.propertyTax / 12))}</span>
          </div>
          <div className="uw-loan-row">
            <span>Other Op. Expenses (mo.)</span>
            <span>{fmt(results.monthlyPropertyOpEx ?? Math.round((s.utilities + s.maintenance + s.reserves) / 12))}</span>
          </div>
          <div className="uw-loan-row uw-loan-row--total">
            <span><strong>Total Monthly Cost</strong></span>
            <span><strong>{fmt(results.totalMonthlyOwnershipCost ?? Math.round((results.annualDebtService + results.totalExpenses) / 12))}</strong></span>
          </div>
          <div className="uw-loan-summary-divider" />
          <div className="uw-loan-summary-title">Financing Detail</div>
          <div className="uw-loan-row">
            <span>Purchase Price</span><span>{fmt(s.purchasePrice)}</span>
          </div>
          <div className="uw-loan-row">
            <span>Loan Amount (70% LTV)</span><span>{fmt(s.loanAmount)}</span>
          </div>
          <div className="uw-loan-row">
            <span>Equity Required</span><span><strong>{fmt(results.equity)}</strong></span>
          </div>
          <div className="uw-loan-row">
            <span>Interest Rate</span><span>{(s.interestRate * 100).toFixed(2)}%</span>
          </div>
          <div className="uw-loan-row">
            <span>Break-Even Occupancy</span>
            <span>{results.breakEvenOccupancy != null ? (results.breakEvenOccupancy * 100).toFixed(1) + '%' : '—'}</span>
          </div>
        </div>
      </div>

    </div>
  )
}

function WRow({ label, value, type }) {
  const cls = {
    income:  'uw-wf-row--income',
    deduct:  'uw-wf-row--deduct',
    expense: 'uw-wf-row--expense',
    debt:    'uw-wf-row--debt',
  }[type] || ''
  return (
    <div className={`uw-wf-row ${cls}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  )
}
